import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Checkbox,
  Autocomplete,
  FormHelperText,
  Grid,
} from "@mui/material";

import {
  GetShipmentQuery,
  NoteAccessLevel,
  DocumentType,
} from "../../../../graphql/generated";
import UploadButton from "../../../common/UploadButton/UploadButton";
import {
  FormError,
  ShipmentDocumentInputData,
} from "../../../../redux/slices/Types";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import documentTypeLabel from "../../../../utils/labels/documentTypeLabel";
import { testIds } from "./ShipmentDocumentForm.testIds";

interface ShipmentDocumentFormProps {
  id: number;
  document: ShipmentDocumentInputData;
  onUpload: (id: number, files: File | null) => void;
  onChange: (id: number, document: ShipmentDocumentInputData) => void;
  shipmentLocations: GetShipmentQuery["shipmentById"]["shipmentLocations"];
  errors?: FormError;
  customerDocumentRequirements?: GetShipmentQuery["shipmentById"]["customer"]["documentRequirements"];
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 200,
    },
  },
};

export default function ShipmentDocumentForm({
  id,
  document,
  shipmentLocations,
  onChange,
  onUpload,
  errors,
  customerDocumentRequirements,
}: ShipmentDocumentFormProps) {
  const { t } = useTranslation(["common", "orders"]);
  const [formErrors, setFormErrors] = useState<FormError | undefined>(errors);

  useEffect(() => {
    setFormErrors(errors);
  }, [errors]);

  const removeError = (name: string | string[]) => {
    const namesToRemove = Array.isArray(name) ? name : [name];
    const err: FormError = {
      formId: formErrors?.formId || "",
      error: _.omit(formErrors?.error, namesToRemove),
    };
    setFormErrors(err);
  };

  // Updated to only handle partial document updates
  const handleChange = (partialDoc: Partial<ShipmentDocumentInputData>) => {
    const fieldsToUpdate = Object.keys(partialDoc);

    // Clear any errors for the fields being updated
    removeError(fieldsToUpdate);

    // Special handling for receiver/shipper exclusivity
    if (partialDoc.receiver && document.shipper) {
      partialDoc.shipper = null;
    } else if (partialDoc.shipper && document.receiver) {
      partialDoc.receiver = null;
    }

    // Apply the changes
    onChange(id, { ...document, ...partialDoc });
  };

  const handleAccessLevelChange = (
    event: SelectChangeEvent<NoteAccessLevel[]>
  ) => {
    const {
      target: { value },
    } = event;

    handleChange({
      accessLevel: typeof value === "string" ? document.accessLevel : value,
    });
  };

  const handleUpload = (file: File | null) => {
    removeError("url");
    onUpload(id, file);
  };

  const shippersAndReceivers = shipmentLocations.map((location) => ({
    kind: location.receiver ? "receiver" : "shipper",
    name: location.receiver ? location.receiver?.name : location.shipper?.name,
    id: location.receiver ? location.receiver?._id : location.shipper?._id,
  }));

  return (
    <Stack component="form" spacing={5}>
      <Grid
        container
        spacing={1}
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "center" }}
      >
        <Grid item xs={12} md={5}>
          <UploadButton data-testid="shipment-document-form-upload-document" onUpload={handleUpload} />
          {formErrors && formErrors.error["url"] && (
            <FormHelperText sx={{ ml: 2 }} error>
              {formErrors.error["url"]}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete data-testid={testIds.shipmentDocumentFormNamePicker}
            // disablePortal
            options={Object.values(DocumentType).map((type) => ({
              label: documentTypeLabel(type),
              value: type,
            }))}
            onChange={(
              event: React.SyntheticEvent<Element, Event>,
              value: any
            ) => {
              if (!value) return;

              // Handle custom input (user typed something not in the list)
              if (typeof value === "string") {
                handleChange({
                  name: value,
                  type: DocumentType.Other,
                  // For custom documents, default to not billable
                  isBillable: false,
                  isForRatecons: false,
                  isForReceivers: false,
                });
                return;
              }

              // Handle selection from dropdown (object with label, value)
              const documentType = value.value as DocumentType;
              const updates: Partial<ShipmentDocumentInputData> = {
                name: value.label,
                type: documentType,
              };

              // Check if this document type has a requirement that specifies attachToInvoice
              if (customerDocumentRequirements?.length) {
                const matchingRequirement = customerDocumentRequirements.find(
                  (req) => req.documentType === documentType
                );

                // If there's a matching requirement, set isBillable based on attachToInvoice
                if (matchingRequirement) {
                  updates.isBillable = matchingRequirement.attachToInvoice;
                }
              }

              handleChange(updates);
            }}
            freeSolo
            getOptionLabel={(option) => {
              // Handle when option is a string (user input)
              if (typeof option === "string") {
                return option;
              }
              // Handle when option is one of our predefined options
              return option.label;
            }}
            value={
              // Try to match with predefined options first
              Object.values(DocumentType)
                .map((type) => ({
                  label: documentTypeLabel(type),
                  value: type,
                }))
                .find((option) => option.label === document.name) ||
              // Otherwise use document name or empty string
              document.name ||
              ""
            }
            size="small"
            renderInput={(renderParams) => (
              <TextField
                required
                name="name"
                error={formErrors && formErrors.error["name"] ? true : false}
                helperText={formErrors && formErrors.error["name"]}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const newValue = event.target.value;

                  // A single update for both fields
                  const updates: Partial<ShipmentDocumentInputData> = {
                    name: newValue,
                  };

                  // If user is manually typing and it's not a predefined type, set type to OTHER
                  if (
                    newValue &&
                    !Object.values(DocumentType)
                      .map((type) => documentTypeLabel(type))
                      .includes(newValue)
                  ) {
                    updates.type = DocumentType.Other;
                  }

                  handleChange(updates);
                }}
                {...renderParams}
                inputProps={{ ...renderParams.inputProps, "data-testid": testIds.shipmentDocumentFormNameSearch }}
                label={t("common:documents.titleLabel", "Document Label")}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3} spacing={2} direction="column">
          <FormControlLabel
            control={
              <Checkbox
                name="isBillable"
                checked={!!document.isBillable}
                onChange={() =>
                  handleChange({ isBillable: !document.isBillable })
                }
              inputProps={{ "data-testid": testIds.shipmentDocumentFormIncludeForInvoicing } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
            label={t(
              "orders:documents.includeForInvoice",
              "Include for Invoicing"
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="isForRatecons"
                checked={!!document.isForRatecons}
                onChange={() =>
                  handleChange({ isForRatecons: !document.isForRatecons })
                }
              inputProps={{ "data-testid": testIds.shipmentDocumentFormIncludeInRatecon } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
            label={t(
              "orders:documents.includeForRatecon",
              "Include in Ratecon"
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="isForReceivers"
                checked={!!document.isForReceivers}
                onChange={() =>
                  handleChange({ isForReceivers: !document.isForReceivers })
                }
              inputProps={{ "data-testid": testIds.shipmentDocumentFormIncludeOnOrderCompletion } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
            label={t(
              "orders:documents.includeForReceivers",
              "Include on order completion"
            )}
          />
        </Grid>
      </Grid>
      <Stack
        direction={{
          sm: "row",
          xs: "column",
        }}
        spacing={2}
      >
        <TextField
          label={t("orders:notes.associatedLocation", "Associated location")}
          name="shipment"
          fullWidth
          select
          value={document.receiver || document.shipper}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const location = shippersAndReceivers.find(
              (c) => c.id === event.target.value
            );
            if (location) {
              const updates: Partial<ShipmentDocumentInputData> = {};
              if (location.kind === "receiver") {
                updates.receiver = location.id;
                updates.shipper = null;
              } else {
                updates.shipper = location.id;
                updates.receiver = null;
              }
              handleChange(updates);
            }
          }}
        inputProps={{ "data-testid": testIds.shipmentDocumentFormAssociatedLocation } as React.InputHTMLAttributes<HTMLInputElement>}
        >
          {shippersAndReceivers.map((shipmentLocation, index) => {
            return (
              <MenuItem data-testid={`${testIds.shipmentDocumentFormAssociatedLocationOption}-${shipmentLocation.id}`} key={index} value={shipmentLocation.id}>
                {shipmentLocation.name}
              </MenuItem>
            );
          })}
        </TextField>
        <FormControl fullWidth>
          <InputLabel
            error={formErrors && formErrors.error["accessLevel"] ? true : false}
            id="user-label"
          >
            {t("orders:documents.visibility", "Visibility")}
          </InputLabel>
          <Select data-testid={testIds.shipmentDocumentFormVisibility}
            labelId="user-label"
            id="user"
            multiple
            size="small"
            value={document.accessLevel}
            onChange={handleAccessLevelChange}
            name="accessLevel"
            error={formErrors && formErrors.error["accessLevel"] ? true : false}
            renderValue={(selected) => (
              <Box
                sx={{
                  "& .MuiChip-root": {
                    height: 20,
                  },
                }}
              >
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={`${t(`orders:notes.accessLevel.${value}`, value)}`}
                  />
                ))}
              </Box>
            )}
            input={<OutlinedInput label="Visibility" />}
            MenuProps={MenuProps}
          >
            {Object.values(NoteAccessLevel).map((accessLevel, index) => (
              <MenuItem data-testid={`${testIds.shipmentDocumentFormVisibilityOption}-${accessLevel}`} key={index} value={accessLevel}>
                {t(`orders:notes.accessLevel.${accessLevel}`, accessLevel)}
              </MenuItem>
            ))}
          </Select>
          {formErrors && (
            <FormHelperText error>
              {formErrors.error["accessLevel"]}
            </FormHelperText>
          )}
        </FormControl>
      </Stack>
    </Stack>
  );
}
